import React, { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { ApiService } from "../services/api";
import { AuthContextType, AuthSystemConfig } from "../types/auth";
import { clearActiveFocusSessionSummary, clearActivePresenceSummary } from "../utils/timeTracking";

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [systemConfig, setSystemConfig] = useState<AuthSystemConfig | null>(() => {
    try {
      const cached = localStorage.getItem("systemConfig");
      return cached ? JSON.parse(cached) : null;
    } catch {
      return null;
    }
  });
  const [notifications, setNotifications] = useState<any>(null);
  const [fileUsage, setFileUsage] = useState<{ used: number; total: number; percentage: number } | null>(null);

  useEffect(() => {
    checkAuthStatus();
    loadSystemConfig();
  }, []);

  const checkAuthStatus = () => {
    const token = localStorage.getItem("authToken");
    const userData = localStorage.getItem("userData");

    if (token && userData) {
      try {
        const user = JSON.parse(userData);
        setUser(user);
        setIsAuthenticated(true);

        const storedFileUsage = localStorage.getItem("fileUsage");
        if (storedFileUsage) {
          try {
            setFileUsage(JSON.parse(storedFileUsage));
          } catch (error) {
            console.error("Failed to parse fileUsage from localStorage:", error);
          }
        }
      } catch (error) {
        localStorage.removeItem("authToken");
        localStorage.removeItem("refreshToken");
        localStorage.removeItem("userData");
        localStorage.removeItem("fileUsage");
      }
    }
  };

  useEffect(() => {
    const loadUserData = async () => {
      try {
        const token = localStorage.getItem("authToken");
        if (!token) {
          return;
        }

        const [userData, paymentAccess] = await Promise.all([
          ApiService.getMe(),
          ApiService.getPaymentAccess().catch(() => null),
        ]);

        if (userData) {
          const nextUser = { ...userData, paymentAccess };
          setUser(nextUser);
          localStorage.setItem("userData", JSON.stringify(nextUser));
        }

        if (userData?.sidebarSort && Array.isArray(userData.sidebarSort)) {
          localStorage.setItem("sidebarSort", JSON.stringify(userData.sidebarSort));
          window.dispatchEvent(new CustomEvent("sidebarSortLoaded"));
        }

        if (userData?.notifications) {
          setNotifications(userData.notifications);
        } else {
          setNotifications(null);
        }

        if (userData?.fileUsage) {
          setFileUsage(userData.fileUsage);
          localStorage.setItem("fileUsage", JSON.stringify(userData.fileUsage));
        } else {
          setFileUsage(null);
          localStorage.removeItem("fileUsage");
        }
      } catch (error) {
        console.error("Failed to load user data:", error);
      }
    };

    if (isAuthenticated && user?.id) {
      loadUserData();
    }

    const handleFileUsageUpdate = () => {
      refreshUserData();
    };

    window.addEventListener("fileUsageUpdated", handleFileUsageUpdate);

    return () => {
      window.removeEventListener("fileUsageUpdated", handleFileUsageUpdate);
    };
  }, [isAuthenticated, user?.id]);

  const loadSystemConfig = async () => {
    try {
      const cachedConfig = localStorage.getItem("systemConfig");
      if (cachedConfig) {
        try {
          const parsedConfig = JSON.parse(cachedConfig);
          setSystemConfig(parsedConfig);
        } catch (error) {
          console.error("Failed to parse cached system config:", error);
        }
      }

      const config = await ApiService.getAuthSystemConfig();

      if (!cachedConfig || JSON.stringify(config) !== cachedConfig) {
        setSystemConfig(config);
        localStorage.setItem("systemConfig", JSON.stringify(config));
      }
    } catch (error) {
      console.error("Failed to load system config:", error);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email: string, password: string) => {
    try {
      setLoading(true);
      const response = await ApiService.login({ email, password });
      if (response.token && response.user) {
        localStorage.setItem("authToken", response.token);
        localStorage.setItem("refreshToken", response.refreshToken);
        localStorage.setItem("userData", JSON.stringify(response.user));
        setUser(response.user);
        setIsAuthenticated(true);
        window.dispatchEvent(new CustomEvent("userLoggedIn"));
      } else {
        throw new Error("Giriş başarısız.");
      }
    } catch (error) {
      console.error("Login error:", error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const register = async (data: {
    firstName: string;
    lastName: string;
    email: string;
    password: string;
    rePassword: string;
    birthDate?: string;
    parentEmail?: string;
    phone?: string;
    selectedClassLevel?: string;
    selectedGraduateTrack?: string;
    selectedDepartmentId?: string;
    turnstileToken: string;
  }) => {
    try {
      const response = await ApiService.registerUser(data);
      return response;
    } catch (error) {
      console.error("Register error:", error);
      throw error;
    }
  };

  const refreshUserData = async () => {
    try {
      const token = localStorage.getItem("authToken");
      if (!token) {
        return;
      }

      const [userData, paymentAccess] = await Promise.all([
        ApiService.getMe(),
        ApiService.getPaymentAccess().catch(() => null),
      ]);

      if (userData) {
        const nextUser = { ...userData, paymentAccess };
        setUser(nextUser);
        localStorage.setItem("userData", JSON.stringify(nextUser));
      };

      if (userData?.sidebarSort && Array.isArray(userData.sidebarSort)) {
        localStorage.setItem("sidebarSort", JSON.stringify(userData.sidebarSort));
        window.dispatchEvent(new CustomEvent("sidebarSortLoaded"));
      }

      if (userData?.notifications) {
        setNotifications(userData.notifications);
      } else {
        setNotifications(null);
      }

      if (userData?.fileUsage) {
        setFileUsage(userData.fileUsage);
        localStorage.setItem("fileUsage", JSON.stringify(userData.fileUsage));
      } else {
        setFileUsage(null);
        localStorage.removeItem("fileUsage");
      }
    } catch (error) {
      console.error("Failed to refresh user data:", error);
    }
  };

  const logout = () => {
    void ApiService.closePresenceSession({
      reason: "logout_closed",
      metadata: {
        source: "auth-logout",
      },
    }).catch(() => null);
    clearActiveFocusSessionSummary();
    clearActivePresenceSummary();
    localStorage.removeItem("authToken");
    localStorage.removeItem("refreshToken");
    localStorage.removeItem("userData");
    localStorage.removeItem("userPreferences");
    localStorage.removeItem("sidebarSort");
    localStorage.removeItem("fileUsage");
    setUser(null);
    setIsAuthenticated(false);
    setNotifications(null);
    setFileUsage(null);
    window.dispatchEvent(new CustomEvent("userLoggedOut"));
  };

  const loginWithToken = (token: string, refreshToken: string, userData: any) => {
    localStorage.setItem("authToken", token);
    localStorage.setItem("refreshToken", refreshToken);
    localStorage.setItem("userData", JSON.stringify(userData));

    setUser(userData);
    setIsAuthenticated(true);

    window.dispatchEvent(new CustomEvent("userLoggedIn"));
    refreshUserData();
  };

  const value: AuthContextType = {
    isAuthenticated,
    user,
    login,
    loginWithToken,
    register,
    logout,
    loading,
    systemConfig,
    notifications,
    fileUsage,
    refreshUserData,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
